/**
 * liveReportDetailsAdapter — maps `GET live-reports/:hash` → `UILiveSessionReportDetails`.
 *
 * Ports the grouping / activity / score rules from legacy `LiveReportComponent` (useChapterGroups,
 * LiveReportActivityItem, engagement axes, the V1 notions + session times). API-aware — not imported
 * by the DS.
 */
import { type SemanticColorKey } from "../../../../../design-system/theme/tokens/colors"
import {
    type UIActivityLevel,
    type UIChapter,
    type UIEngagementAlert,
    type UILiveEngagementScore,
    type UILiveSessionReportActivity,
    type UILiveSessionReportChapter,
    type UILiveSessionReportConceptGroup,
    type UILiveSessionReportDetails,
    type UISchoolAssessment,
} from "../../../ui/types"
import { indexSubjectsById, toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

/* -------------------------------------------------------------------------- */
/*  Loose API shapes                                                          */
/* -------------------------------------------------------------------------- */

type ApiScoreDetail = {
    name?: string
    score?: number
    score_max?: number
    value?: string | number
    unity?: string
}

export type ApiAxisScore = {
    score?: number
    score_max?: number
    details?: ApiScoreDetail[]
}

type ApiEngagementAlert = {
    severity?: string
    message?: string
    detail?: string
    recurrence_count?: number
}

type ApiActivityRow = {
    digit?: number
    label?: string
    activity_template_id?: string | number
}

type ApiActivityTemplate = {
    id?: string | number
    icon?: string
}

type ApiOngoingChapter = {
    chapter_id?: string | number
    level?: number
    xp_gained?: number
    xp_total?: number
    validated_activities_count?: number
    todo_activities_count?: number
    chapter?: {
        id?: string | number
        name?: string
        school_subject_id?: string | number
        chapter_id?: string | number
    } | null
}

type ApiLinkedAssessment = {
    id?: string | number
    title?: string
    date?: string
    days_until?: number
    urgency?: string
    preparation_progress?: number
    matched_chapter_ids?: Array<string | number>
    school_subject?: ApiSchoolSubject | null
}

type ApiConceptGroup = {
    school_subject_id?: string | number
    concepts?: string[] | null
}

/** Loose raw report from `liveReportService.getLiveReport`. */
export type ApiLiveReportDetails = {
    is_trial?: boolean
    satisfaction?: number
    engagement_alerts?: ApiEngagementAlert[] | null
    activity_templates?: ApiActivityTemplate[] | null
    linked_assessments?: ApiLinkedAssessment[] | null
    school_subjects?: ApiSchoolSubject[] | null
    commented_report?: { content?: Record<string, string> | unknown[] | null } | null
    student?: {
        display_name?: string
        email?: string
        firstname?: string
        parent_firstname?: string
        user?: { email?: string } | null
    } | null
    live_occurrence?: { date?: string } | null
    statistics?: {
        live_date_time?: string
        zoe_variant?: string
        CTAS?: { zoe?: string; sam?: string } | null
        scores?: {
            total_score?: number
            assiduity?: ApiAxisScore
            interaction?: ApiAxisScore
            productivity?: ApiAxisScore
        } | null
        ongoing_student_chapters?: ApiOngoingChapter[] | null
        validated_activities?: ApiActivityRow[] | null
        todo_activities?: ApiActivityRow[] | null
        /** Backend groups by subject id, so this arrives keyed rather than as a list. */
        concepts?: ApiConceptGroup[] | Record<string, ApiConceptGroup> | null
        /** The three session durations, in seconds. */
        live_connection_duration?: number
        tutor_call_duration?: number
        duration?: number
    } | null
}

const AXIS_META: Record<
    "assiduity" | "interaction" | "productivity",
    { label: string; max: number; color: SemanticColorKey }
> = {
    assiduity: { label: "Présence", max: 40, color: "success" },
    interaction: { label: "Participation", max: 30, color: "phone" },
    productivity: { label: "Productivité", max: 30, color: "purple" },
}

const ABSENT_HERO = "Malheureusement, nous n’avons pas eu la chance de te voir aujourd’hui. Quel dommage..."

const URGENCIES = new Set(["low", "medium", "high", "critical"])

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * V1 reports lack `ongoing_student_chapters` on statistics (legacy `isV1Report`). Read on the
 * detail and on the list rows alike — both carry the same `statistics` blob.
 */
export function isV1LiveReport(raw: { statistics?: object | null } | null | undefined): boolean {
    if (!raw?.statistics) return false
    return !("ongoing_student_chapters" in raw.statistics)
}

export function mapAxis(key: keyof typeof AXIS_META, axis: ApiAxisScore | undefined): UILiveEngagementScore {
    const meta = AXIS_META[key]
    return {
        label: meta.label,
        value: axis?.score ?? 0,
        max: axis?.score_max ?? meta.max,
        color: meta.color,
        details: (axis?.details ?? []).map((d) => {
            const parts = [d.value, d.unity].filter((x) => x != null && String(x) !== "")
            return {
                name: d.name ?? "",
                score: d.score,
                scoreMax: d.score_max,
                comment: parts.length > 0 ? parts.join(" ") : undefined,
            }
        }),
    }
}

function mapAlerts(raw: ApiEngagementAlert[] | null | undefined): UIEngagementAlert[] {
    return (raw ?? [])
        .map((a): UIEngagementAlert | null => {
            if (!a?.message) return null
            const severity = a.severity === "alert" ? "alert" : "warning"
            return {
                severity,
                message: a.message,
                detail: a.detail || undefined,
                recurrenceCount: a.recurrence_count ?? undefined,
            }
        })
        .filter((a): a is UIEngagementAlert => a != null)
}

function mapCommented(
    content: ApiLiveReportDetails["commented_report"]
): UILiveSessionReportDetails["commentedReport"] {
    const raw = content?.content
    if (!raw || Array.isArray(raw) || typeof raw !== "object") return undefined
    const c = raw as Record<string, string>
    const out = {
        strengths: c.strengths || undefined,
        challenges: c.challenges || undefined,
        progress: c.progress || undefined,
        nextGoal: c.next_goal || undefined,
    }
    if (!out.strengths && !out.challenges && !out.progress && !out.nextGoal) return undefined
    return out
}

function mapActivities(
    rows: ApiActivityRow[] | null | undefined,
    templates: ApiActivityTemplate[] | null | undefined
): UILiveSessionReportActivity[] {
    const byId = new Map((templates ?? []).filter((t) => t?.id != null).map((t) => [String(t.id), t]))
    return (rows ?? [])
        .map((row): UILiveSessionReportActivity | null => {
            if (row?.digit == null || !row?.label) return null
            const tpl = row.activity_template_id != null ? byId.get(String(row.activity_template_id)) : undefined
            return {
                count: row.digit,
                label: row.label,
                iconUrl: tpl?.icon,
            }
        })
        .filter((a): a is UILiveSessionReportActivity => a != null)
}

/**
 * `statistics.concepts` → the "Notions abordées" groups (legacy `LiveReportComponent:1046-1090`).
 *
 * Divergence: a group carrying no notion is dropped — the legacy renders its subject name over an
 * empty line. Nothing is lost, since `liveConcepts()` is no longer called anywhere in the backend
 * and the field is absent from every recent report.
 */
function mapConceptGroups(
    raw: ApiConceptGroup[] | Record<string, ApiConceptGroup> | null | undefined,
    subjectsById: Record<string, ApiSchoolSubject>
): UILiveSessionReportConceptGroup[] {
    const groups = Array.isArray(raw) ? raw : Object.values(raw ?? {})
    return groups
        .map((group): UILiveSessionReportConceptGroup | null => {
            const concepts = (group?.concepts ?? []).filter((c) => typeof c === "string" && c !== "")
            if (concepts.length === 0) return null
            const subjectId = group.school_subject_id
            return {
                subject: toUISubject(subjectId != null ? subjectsById[String(subjectId)] : undefined),
                concepts,
            }
        })
        .filter((g): g is UILiveSessionReportConceptGroup => g != null)
}

function toActivityLevel(level: number | undefined): UIActivityLevel | undefined {
    if (level === 1 || level === 2 || level === 3) return level
    return undefined
}

function mapReportChapter(
    item: ApiOngoingChapter,
    subjectsById: Record<string, ApiSchoolSubject>
): UILiveSessionReportChapter | null {
    const merged = { ...item, ...(item.chapter ?? {}) }
    const id = merged.chapter_id ?? merged.id ?? item.chapter?.id
    const name = item.chapter?.name ?? (merged as { name?: string }).name
    if (id == null || !name) return null

    const subjectId =
        item.chapter?.school_subject_id ?? (merged as { school_subject_id?: string | number }).school_subject_id
    const chapter: UIChapter = {
        id,
        name,
        subject: toUISubject(subjectId != null ? subjectsById[String(subjectId)] : undefined),
    }

    return {
        chapter,
        validatedCount: item.validated_activities_count ?? 0,
        todoCount: item.todo_activities_count ?? 0,
        level: toActivityLevel(item.level),
        xpGained: item.xp_gained,
        xpTotal: item.xp_total,
    }
}

/**
 * Literal port of legacy `useChapterGroups` + orphan/assessment titles.
 */
function mapChapterSections(
    chapters: ApiOngoingChapter[] | null | undefined,
    linkedAssessments: ApiLinkedAssessment[] | null | undefined,
    subjectsById: Record<string, ApiSchoolSubject>
): UILiveSessionReportDetails["chapterSections"] {
    const chapterToAssessment: Record<string, string | number> = {}
    const assessmentGroups: Record<string, { assessment: ApiLinkedAssessment; chapters: ApiOngoingChapter[] }> = {}

    if (linkedAssessments && linkedAssessments.length > 0) {
        linkedAssessments.forEach((assessment) => {
            if (assessment?.id == null) return
            assessmentGroups[String(assessment.id)] = { assessment, chapters: [] }
            assessment.matched_chapter_ids?.forEach((chapterId) => {
                chapterToAssessment[String(chapterId)] = assessment.id as string | number
            })
        })
    }

    const orphanItems: ApiOngoingChapter[] = []

    chapters?.forEach((item) => {
        const merged = { ...item, ...(item.chapter ?? {}) }
        const chapterId = merged.chapter_id ?? merged.id ?? item.chapter?.id
        if (chapterId == null) {
            orphanItems.push(item)
            return
        }
        const assessmentId = chapterToAssessment[String(chapterId)]
        if (assessmentId != null && assessmentGroups[String(assessmentId)]) {
            assessmentGroups[String(assessmentId)].chapters.push(item)
        } else {
            orphanItems.push(item)
        }
    })

    const sortedGroups = Object.values(assessmentGroups)
        .filter((group) => group.chapters.length > 0)
        .sort((a, b) => (a.assessment.days_until ?? 0) - (b.assessment.days_until ?? 0))

    const sections: UILiveSessionReportDetails["chapterSections"] = []

    for (const group of sortedGroups) {
        const mappedChapters = group.chapters
            .map((c) => mapReportChapter(c, subjectsById))
            .filter((c): c is UILiveSessionReportChapter => c != null)
        if (mappedChapters.length === 0) continue

        sections.push({
            title: "Préparation du contrôle",
            assessment: toUILinkedAssessment(group.assessment, mappedChapters),
            chapters: mappedChapters,
        })
    }

    if (orphanItems.length > 0) {
        const mappedOrphans = orphanItems
            .map((c) => mapReportChapter(c, subjectsById))
            .filter((c): c is UILiveSessionReportChapter => c != null)
        if (mappedOrphans.length > 0) {
            sections.push({
                title: sortedGroups.length > 0 ? "Autres chapitres" : "Avancement des chapitres",
                chapters: mappedOrphans,
            })
        }
    }

    return sections
}

function toUILinkedAssessment(
    assessment: ApiLinkedAssessment,
    sectionChapters: UILiveSessionReportChapter[]
): UISchoolAssessment {
    const subject = toUISubject(assessment.school_subject)
    const progress = assessment.preparation_progress ?? 0
    // Linked-assessment payload only carries overall `preparation_progress` — feed one row so the
    // DS average (`preparationProgress`) equals the legacy bar %.
    const chaptersProgress =
        sectionChapters.length > 0
            ? [{ chapter: sectionChapters[0].chapter, progress }]
            : [{ chapter: { id: assessment.id ?? 0, name: assessment.title ?? "", subject }, progress }]

    const urgencyRaw = assessment.urgency ?? "medium"
    const urgency = (URGENCIES.has(urgencyRaw) ? urgencyRaw : "medium") as UISchoolAssessment["urgency"]

    return {
        id: assessment.id ?? 0,
        title: assessment.title ?? "",
        date: assessment.date ?? "",
        subject,
        chaptersProgress,
        urgency,
    }
}

/* -------------------------------------------------------------------------- */
/*  Public mapper                                                             */
/* -------------------------------------------------------------------------- */

export function toUILiveSessionReportDetails(
    raw: ApiLiveReportDetails | null | undefined,
    apiSubjects: ApiSchoolSubject[] | null | undefined
): UILiveSessionReportDetails | null {
    if (!raw) return null

    const stats = raw.statistics
    const isAbsent = !stats
    const isTrial = raw.is_trial === true
    // Legacy: engagement report = loaded non-trial (`is_trial === false`).
    const isEngagementReport = raw.is_trial === false
    const v1 = isV1LiveReport(raw)

    const subjectsById = indexSubjectsById(raw.school_subjects?.length ? raw.school_subjects : apiSubjects)

    const showSamCalendlyCta = !!stats?.CTAS?.sam

    let heroMessage: string | undefined
    if (isAbsent) {
        heroMessage = ABSENT_HERO
    } else if (!isEngagementReport) {
        heroMessage = stats?.CTAS?.zoe || undefined
    }

    const scores =
        !isAbsent && !v1 && stats?.scores
            ? {
                  total: stats.scores.total_score ?? 0,
                  assiduity: mapAxis("assiduity", stats.scores.assiduity),
                  interaction: mapAxis("interaction", stats.scores.interaction),
                  productivity: mapAxis("productivity", stats.scores.productivity),
                  alerts: isEngagementReport ? mapAlerts(raw.engagement_alerts) : [],
              }
            : undefined

    const chapterSections =
        !isAbsent && !v1
            ? mapChapterSections(stats?.ongoing_student_chapters, raw.linked_assessments, subjectsById)
            : []

    const validatedActivities = !isAbsent ? mapActivities(stats?.validated_activities, raw.activity_templates) : []
    const todoActivities = !isAbsent ? mapActivities(stats?.todo_activities, raw.activity_templates) : []

    // Legacy gates "Notions abordées" on the activity count as well as on V1 (`LiveReportComponent:1035`).
    const showConcepts = v1 && validatedActivities.length + todoActivities.length > 0
    const conceptGroups = showConcepts ? mapConceptGroups(stats?.concepts, subjectsById) : undefined

    // The three durations reach a V2 report too, but the legacy only renders them on a V1 one
    // (`LiveReportComponent:1153`) — showing them elsewhere is a product call, not a mapping one.
    const sessionTimes = v1
        ? {
              liveConnection: stats?.live_connection_duration ?? 0,
              tutorCall: stats?.tutor_call_duration ?? 0,
              production: stats?.duration ?? 0,
          }
        : undefined

    return {
        date: stats?.live_date_time ?? raw.live_occurrence?.date ?? "",
        studentDisplayName: raw.student?.display_name ?? "",
        zoeVariant: stats?.zoe_variant ?? (isAbsent ? "main" : "happy"),
        isTrial,
        heroMessage,
        showSamCalendlyCta,
        samMessage: stats?.CTAS?.sam || undefined,
        satisfaction: raw.satisfaction,
        commentedReport: mapCommented(raw.commented_report),
        scores,
        chapterSections,
        validatedActivities,
        todoActivities,
        conceptGroups,
        sessionTimes,
    }
}
